/**
 * Centralized Google Maps API Loader
 * Ensures only ONE instance of Google Maps is loaded with the API key from admin settings
 */

import axios from 'axios';

let isLoading = false;
let isLoaded = false;
let apiKey = null;
const callbacks = [];

/**
 * Load Google Maps API script with key from admin settings
 * @param {Function} callback - Function to call when script is loaded
 */
export const loadGoogleMapsScript = async (callback) => {
  // If already loaded, call callback immediately
  if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
    console.log('Google Maps already loaded, calling callback');
    if (callback) callback();
    return;
  }

  // Add callback to queue
  if (callback) {
    callbacks.push(callback);
  }

  // If already loading, wait for it
  if (isLoading) {
    console.log('Google Maps is already loading, callback queued');
    return;
  }

  isLoading = true;

  try {
    // Try environment variable first
    apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // If no env var, fetch from API endpoint
    if (!apiKey) {
      console.log('No env var, fetching Google Maps API key from server...');
      try {
        const token = localStorage.getItem('shootzu_token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/googlemaps-key`,
          token ? { headers: { 'Authorization': `Bearer ${token}` } } : {}
        );
        apiKey = response.data?.apiKey || response.data?.api_key;
        console.log('Fetched API key from server:', apiKey ? 'success' : 'empty');
      } catch (fetchError) {
        console.error('Failed to fetch Google Maps API key from server:', fetchError);
      }
    }

    if (!apiKey) {
      console.error('Google Maps API key not found');
      isLoading = false;
      // Still call callbacks so they know loading failed
      callbacks.forEach(cb => {
        try {
          cb();
        } catch (error) {
          console.error('Error in Google Maps callback:', error);
        }
      });
      callbacks.length = 0;
      return;
    }

    console.log('Loading Google Maps with API key');

    // Remove any existing Google Maps scripts (cleanup)
    const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    existingScripts.forEach(script => {
      console.log('Removing existing Google Maps script');
      script.remove();
    });

    // Create and load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('Google Maps script loaded successfully');
      isLoaded = true;
      isLoading = false;

      // Call all queued callbacks
      callbacks.forEach(cb => {
        try {
          cb();
        } catch (error) {
          console.error('Error in Google Maps callback:', error);
        }
      });
      callbacks.length = 0;
    };

    script.onerror = () => {
      console.error('Failed to load Google Maps script');
      isLoading = false;
      callbacks.length = 0;
    };

    document.head.appendChild(script);
  } catch (error) {
    console.error('Error loading Google Maps:', error);
    isLoading = false;
    callbacks.length = 0;
  }
};

/**
 * Check if Google Maps is loaded
 */
export const isGoogleMapsLoaded = () => {
  return isLoaded && window.google && window.google.maps && window.google.maps.places;
};

/**
 * Get the current API key
 */
export const getApiKey = () => apiKey;
